/**
 * Price formatting library for parsing and formatting currency values
 */

/**
 * Parses a formatted price string and extracts formatting information
 * @param {string} formattedValue - The formatted price string (e.g., "$1,234.56", "1.234,56 €")
 * @returns {Object} Object containing value and format info
 */
export function parsePriceWithFormat(formattedValue) {
    // Extract currency symbol (any non-digit, non-space, non-separator characters)
    const currencyMatch = formattedValue.match(/[^\d\s.,]+/);
    const currency = currencyMatch ? currencyMatch[0] : '';
    const currencyAtEnd = currency
        ? formattedValue.indexOf(currency) > formattedValue.length / 2
        : false;

    // Remove currency symbol and trim
    const numericPart = formattedValue.replace(currency, '').trim();

    // Detect all separators
    const separators = numericPart.match(/[.,]/g) || [];

    // Last separator is decimal, others are thousands
    const decimalSep = separators.length > 0 ? separators[separators.length - 1] : '.';
    const thousandSep = separators.length > 1 ? separators[0] : '';

    // Count decimal places
    const lastSepPos = numericPart.lastIndexOf(decimalSep);
    const decimalPlaces = lastSepPos !== -1 ? numericPart.length - lastSepPos - 1 : 2;

    // Parse to float
    let normalized = numericPart.replace(
        new RegExp(escapeRegExp(thousandSep), 'g'),
        ''
    );
    normalized = normalized.replace(decimalSep, '.');
    const value = parseFloat(normalized) || 0;

    return {
        value,
        format: {
            currency,
            currencyAtEnd,
            decimalSep,
            thousandSep,
            decimalPlaces
        }
    };
}

function escapeRegExp(str) {
    return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Converts a formatted price string to a float value
 * @param {string} formattedValue - The formatted price string
 * @returns {number} The numeric value as a float
 */
export function priceToFloat(formattedValue) {
    const { value } = parsePriceWithFormat(formattedValue);
    return value;
}

/**
 * Formats a number using the provided format structure
 * @param {number} value - The numeric value to format
 * @param {Object} format - Format object with currency, separators, etc.
 * @returns {string} Formatted price string
 */
export function formatPrice(value, format) {
    const { currency = '', currencyAtEnd = false, decimalSep = '.', thousandSep = '', decimalPlaces = 2 } = format;

    // Format the number
    const parts = value.toFixed(decimalPlaces).split('.');
    const integerPart = parts[0];
    const decimalPart = parts[1];

    // Add thousand separators
    let formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, thousandSep);

    // Combine with decimal part
    const formatted = decimalPart ? `${formattedInteger}${decimalSep}${decimalPart}` : formattedInteger;

    // Add currency in the correct position
    return currencyAtEnd ? `${formatted}${currency}` : `${currency}${formatted}`;
}

/**
 * Adds an amount to a formatted price while preserving the original formatting
 * @param {string} formattedValue - The original formatted price
 * @param {number|null} amountToAdd - The amount to add (null to reset to 0)
 * @returns {string} The result formatted in the same style as the input
 */
export function addAmountWhileKeepingFormatting(formattedValue, amountToAdd = null) {
    const { value, format } = parsePriceWithFormat(formattedValue);

    // Calculate new value
    const result = amountToAdd === null ? 0 : value + amountToAdd;

    // Format and return
    return formatPrice(result, format);
}

/*
// Example usage:
console.log('=== Example Usage ===\n');

// Parse formatted prices to float
console.log('Parsing prices to float:');
console.log(priceToFloat('$1,234.56'));        // 1234.56
console.log(priceToFloat('1.234,56 €'));       // 1234.56
console.log(priceToFloat('£ 1,234.56'));       // 1234.56

// Parse with format info
console.log('\nParsing with format info:');
const parsed = parsePriceWithFormat('$1,234.56');
console.log(parsed);
// { value: 1234.56, format: { currency: '$', currencyAtEnd: false, decimalSep: '.', thousandSep: ',', decimalPlaces: 2 } }

// Reuse format for multiple values
console.log('\nReusing format:');
console.log(formatPrice(5000, parsed.format));     // $5,000.00
console.log(formatPrice(999.9, parsed.format));    // $999.90

// Add amounts while keeping formatting
console.log('\nAdding amounts while keeping format:');
console.log(addAmountWhileKeepingFormatting('$1,234.56', 500));    // $1,734.56
console.log(addAmountWhileKeepingFormatting('1.234,56 €', -200));  // 1.034,56 €
console.log(addAmountWhileKeepingFormatting('£ 1,234.56', null));  // £ 0.00

 */